import axios from 'axios';
import Utilities from '../assets/js/Utilities';

// Create axios instance with default config
const axiosInstance = axios.create({
  baseURL: global.config?.api?.domain || '',
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json'
  },
  responseType: 'text' // Get response as text to handle BigInt parsing
});

// Request interceptor to add token to all requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('FWToken');
    if (token) {
      config.headers.Token = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Guard to prevent multiple simultaneous 401s from triggering multiple redirects
let isHandling401 = false;

// Response interceptor to handle 401 errors and parse JSON with BigInt support
axiosInstance.interceptors.response.use(
  (response) => {
    // Parse text response to JSON using BigInt parser
    if (typeof response.data === 'string' && response.data.trim().length > 0) {
      try {
        // Check if response looks like JSON
        const trimmedData = response.data.trim();
        if ((trimmedData.startsWith('{') && trimmedData.endsWith('}')) ||
          (trimmedData.startsWith('[') && trimmedData.endsWith(']'))) {
          const originalData = response.data;
          const parsed = Utilities.JsonParseBigInt(response.data);
          if (parsed !== undefined && parsed !== null) {
            response.data = parsed;
          } else {
            // BigInt parser returned undefined/null (internal error), fallback to JSON.parse
            try {
              response.data = JSON.parse(originalData);
            } catch {
              // Keep response.data as original string if all parsing fails
              response.data = originalData;
            }
          }
        }
        // If it's not JSON-like, keep as string
      } catch (parseError) {
        // Fallback to regular JSON.parse
        try {
          response.data = JSON.parse(response.data);
        } catch {
          // Keep response.data as string if all parsing fails
        }
      }
    }

    return response;
  },
  (error) => {
    // Handle 401 Unauthorized responses
    if (error.response && error.response.status === 401 && !isHandling401) {
      isHandling401 = true;

      console.warn('401 Unauthorized - Token expired or invalid');

      // Clear authentication data
      localStorage.removeItem('FWToken');
      localStorage.removeItem('FWUser');

      // Dispatch logout event to update app state
      window.dispatchEvent(new Event('logout'));

      // Build login URL using app basename (supports proxy/alias deployments)
      const basename = (typeof window.getBasename === 'function' ? window.getBasename() : '') || '';
      const currentPath = window.location.pathname;
      const loginPath = `${basename}/login`;
      const redirectPath = `${window.location.origin}${loginPath}`;

      // Avoid redirect loop if we're already on the login page
      const alreadyOnLogin = currentPath === loginPath || currentPath.endsWith('/login');
      if (!alreadyOnLogin) {
        console.log('Session expired. Redirecting to login...');
        // Use replace() so the 401'd page isn't kept in history
        window.location.replace(redirectPath);
      } else {
        isHandling401 = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
